import React from 'react';
import { Layout } from '../components/Layout';
import {Smartphone, Calendar, ShoppingCart, CreditCard, QrCode, Bell, MapPin, Download} from 'lucide-react';
import { Link } from 'react-router-dom';

const AppPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-black via-zinc-900 to-black">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-nolimit-red rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-600 rounded-full blur-[120px]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block bg-nolimit-red/10 border border-nolimit-red/20 px-4 py-2 rounded-full mb-6">
                <span className="text-nolimit-red font-bold text-sm uppercase tracking-wider">Application Mobile</span>
              </div>
              <h1 className="text-6xl md:text-7xl font-black text-white mb-6 tracking-tighter italic leading-tight">
                VOTRE CLUB
                <br />
                <span className="text-nolimit-red">DANS VOTRE POCHE</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Téléchargez l'application <strong>No Limit Fitness</strong> et profitez d'une expérience simplifiée : réservez vos cours, gérez vos abonnements et accédez à la salle en un clic.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a 
                  href="https://play.google.com/store" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-black border-2 border-white/20 hover:border-nolimit-red text-white px-8 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all transform hover:scale-105"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  <span>Google Play</span>
                </a>
                <a 
                  href="https://apps.apple.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white hover:bg-gray-100 text-black px-8 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all transform hover:scale-105"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                  </svg>
                  <span>App Store</span>
                </a>
              </div>

              <div className="flex items-center gap-4 text-gray-400 text-sm">
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-nolimit-red" />
                  <span>Gratuit</span>
                </div>
                <div className="w-px h-4 bg-gray-600" />
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-nolimit-red" />
                  <span>iOS & Android</span>
                </div>
              </div>
            </div>

            {/* Phone Mockup avec premier screen */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-nolimit-red/20 rounded-[3rem] blur-3xl scale-110" />
                
                {/* Phone Frame */}
                <div className="relative bg-zinc-900 rounded-[3rem] p-3 shadow-2xl border-4 border-zinc-800 transform hover:scale-105 transition-transform duration-300">
                  <div className="bg-black rounded-[2.5rem] overflow-hidden">
                    {/* Notch */}
                    <div className="bg-black h-8 flex items-center justify-center">
                      <div className="w-32 h-6 bg-zinc-900 rounded-full" />
                    </div>
                    
                    {/* Screen Content - Home Screen */}
                    <img 
                      src="https://static.lumi.new/22/22ff3c2e89060c03b3cc62bf7b44cc04.webp"
                      alt="App Home Screen"
                      className="w-full h-[600px] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fonctionnalités avec Mockups 3D */}
      <section className="py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-black italic text-white mb-4">
              TOUTES LES FONCTIONNALITÉS
              <br />
              <span className="text-nolimit-red">EN UN SEUL ENDROIT</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Gérez votre expérience fitness comme jamais auparavant
            </p>
          </div>

          {/* Feature 1 - Réservation de cours */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 px-4 py-2 rounded-full mb-6">
                <Calendar className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 font-bold text-sm uppercase">Planning</span>
              </div>
              <h3 className="text-4xl font-black text-white mb-6 italic">
                RÉSERVEZ VOS <span className="text-nolimit-red">COURS</span>
              </h3>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Consultez le planning en temps réel et réservez vos cours collectifs en quelques secondes. Interval Training, Pilate, Attack, Energy Jumping... Ne ratez plus jamais votre séance !
              </p>
              <ul className="space-y-4">
                {[
                  "Planning en temps réel",
                  "Réservation instantanée",
                  "Nombre de participants visible",
                  "Notifications de rappel"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-200">
                    <div className="w-2 h-2 bg-nolimit-red rounded-full" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-500/20 rounded-[3rem] blur-3xl" />
                <div className="relative bg-zinc-800 rounded-[3rem] p-3 shadow-2xl border-4 border-zinc-700 transform hover:rotate-2 transition-all duration-300">
                  <div className="bg-black rounded-[2.5rem] overflow-hidden">
                    <div className="bg-black h-8 flex items-center justify-center">
                      <div className="w-32 h-6 bg-zinc-900 rounded-full" />
                    </div>
                    <img 
                      src="https://static.lumi.new/c1/c1f2ffbe7e8970319781186ede8258fd.webp"
                      alt="Planning des cours"
                      className="w-full h-[600px] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2 - Boutique & Abonnements */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-teal-500/20 rounded-[3rem] blur-3xl" />
                <div className="relative bg-zinc-800 rounded-[3rem] p-3 shadow-2xl border-4 border-zinc-700 transform hover:-rotate-2 transition-all duration-300">
                  <div className="bg-black rounded-[2.5rem] overflow-hidden">
                    <div className="bg-black h-8 flex items-center justify-center">
                      <div className="w-32 h-6 bg-zinc-900 rounded-full" />
                    </div>
                    <img 
                      src="https://static.lumi.new/c6/c654fdc21848ca9459931a3618bb8c76.webp"
                      alt="Boutique"
                      className="w-full h-[600px] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 px-4 py-2 rounded-full mb-6">
                <ShoppingCart className="w-4 h-4 text-teal-400" />
                <span className="text-teal-400 font-bold text-sm uppercase">Boutique</span>
              </div>
              <h3 className="text-4xl font-black text-white mb-6 italic">
                GÉREZ VOS <span className="text-nolimit-red">ABONNEMENTS</span>
              </h3>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Achetez directement depuis l'app : abonnements mensuels, carnets de séances, ou le Xplor Pass pour un accès simple et rapide à la salle.
              </p>
              <ul className="space-y-4">
                {[
                  "Abonnements flexibles (mois, trimestre, semestre, année)",
                  "Xplor Pass à 15€ pour l'accès physique",
                  "Carnets de séances",
                  "Paiement sécurisé dans l'app"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-200">
                    <div className="w-2 h-2 bg-nolimit-red rounded-full" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Feature 3 - Accès QR Code */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-nolimit-red/10 border border-nolimit-red/20 px-4 py-2 rounded-full mb-6">
                <QrCode className="w-4 h-4 text-nolimit-red" />
                <span className="text-nolimit-red font-bold text-sm uppercase">Accès Club</span>
              </div>
              <h3 className="text-4xl font-black text-white mb-6 italic">
                ACCÈS <span className="text-nolimit-red">SANS CONTACT</span>
              </h3>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Scannez le QR code depuis l'app pour accéder à la salle. Plus besoin de badge physique, votre smartphone devient votre pass d'entrée !
              </p>
              <ul className="space-y-4">
                {[
                  "QR code personnel intégré",
                  "Accès hygiénique et rapide",
                  "Historique de vos visites",
                  "Gestion de plusieurs comptes (famille)"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-200">
                    <div className="w-2 h-2 bg-nolimit-red rounded-full" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-nolimit-red/20 rounded-[3rem] blur-3xl" />
                <div className="relative bg-zinc-800 rounded-[3rem] p-3 shadow-2xl border-4 border-zinc-700 transform hover:rotate-2 transition-all duration-300">
                  <div className="bg-black rounded-[2.5rem] overflow-hidden">
                    <div className="bg-black h-8 flex items-center justify-center">
                      <div className="w-32 h-6 bg-zinc-900 rounded-full" />
                    </div>
                    <img 
                      src="https://static.lumi.new/0f/0f0c602bb86052b54733ba45e8c4e615.webp"
                      alt="Affiche QR Codes"
                      className="w-full h-[600px] object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Toutes les fonctionnalités */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black italic text-white mb-4">
              ET BIEN <span className="text-nolimit-red">PLUS ENCORE</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Bell className="w-8 h-8" />,
                title: "Notifications Push",
                desc: "Recevez des rappels pour vos cours et les nouveautés du club"
              },
              {
                icon: <CreditCard className="w-8 h-8" />,
                title: "Paiement Mobile",
                desc: "Payez vos abonnements directement depuis l'application"
              },
              {
                icon: <MapPin className="w-8 h-8" />,
                title: "Localisation",
                desc: "Trouvez facilement le club avec l'itinéraire intégré"
              },
              {
                icon: <Calendar className="w-8 h-8" />,
                title: "Historique",
                desc: "Consultez l'historique de vos réservations et achats"
              },
              {
                icon: <Smartphone className="w-8 h-8" />,
                title: "Multi-Comptes",
                desc: "Gérez plusieurs comptes club depuis une seule app"
              },
              {
                icon: <QrCode className="w-8 h-8" />,
                title: "Badge Numérique",
                desc: "Votre téléphone devient votre badge d'accès"
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-zinc-900 p-8 rounded-2xl border border-white/5 hover:border-nolimit-red/50 transition-all group">
                <div className="w-16 h-16 bg-nolimit-red/10 rounded-xl flex items-center justify-center mb-6 text-nolimit-red group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inscription avec QR Code */}
      <section className="py-24 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-black italic text-white mb-6 leading-tight">
                COMMENT
                <br />
                <span className="text-nolimit-red">S'INSCRIRE ?</span>
              </h2>
              <div className="space-y-6 mb-10">
                {[
                  { step: "1", text: "Scannez le QR code avec votre smartphone" },
                  { step: "2", text: "Téléchargez l'app (Android ou iOS)" },
                  { step: "3", text: "Saisissez votre adresse mail" },
                  { step: "4", text: "Entrez le code de confirmation" },
                  { step: "5", text: "Tapez le code d'accès : no-limit-fitness" },
                  { step: "6", text: "Remplissez vos informations personnelles" },
                  { step: "7", text: "Choisissez votre abonnement et mode de paiement" }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-nolimit-red rounded-full flex items-center justify-center font-black text-black shrink-0">
                      {item.step}
                    </div>
                    <p className="text-gray-200 text-lg pt-2">{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
                <p className="text-yellow-400 font-bold mb-2">⚠️ Important</p>
                <p className="text-gray-300">
                  Pour finaliser, récupérez votre badge d'accès (10€) directement à la salle !
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/20 rounded-[3rem] blur-3xl" />
                <div className="relative bg-zinc-800 rounded-[3rem] p-3 shadow-2xl border-4 border-zinc-700">
                  <div className="bg-black rounded-[2.5rem] overflow-hidden">
                    <div className="bg-black h-8 flex items-center justify-center">
                      <div className="w-32 h-6 bg-zinc-900 rounded-full" />
                    </div>
                    <img 
                      src="https://static.lumi.new/b2/b2bc8e1bc4333979d20ae5dc0473447b.webp"
                      alt="Paramètres App"
                      className="w-full h-[600px] object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-nolimit-red">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Smartphone className="h-20 w-20 text-black mx-auto mb-8" />
          <h2 className="text-5xl font-black text-black mb-6 italic uppercase">
            Téléchargez l'app maintenant
          </h2>
          <p className="text-xl text-white/90 mb-12 font-medium">
            Rejoignez les centaines de membres qui utilisent déjà l'application No Limit Fitness
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-8">
            <a 
              href="https://play.google.com/store" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-black hover:bg-zinc-900 text-white px-10 py-5 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all transform hover:scale-105 shadow-2xl"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              <div className="text-left">
                <div className="text-xs text-gray-400">Télécharger sur</div>
                <div className="font-black">Google Play</div>
              </div>
            </a>
            <a 
              href="https://apps.apple.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white hover:bg-gray-100 text-black px-10 py-5 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all transform hover:scale-105 shadow-2xl"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
              </svg>
              <div className="text-left">
                <div className="text-xs text-gray-500">Télécharger sur</div>
                <div className="font-black">App Store</div>
              </div>
            </a>
          </div>

          <p className="text-white/80 text-sm">
            Application gratuite • Compatible iOS et Android • En français
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default AppPage;